import { navigate } from "@reach/router";
import { graphql, useStaticQuery } from "gatsby";
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";

import CtaButton from "../../../components/shared/buttons/ctaButton";
import ProgramRating from "../../../components/shared/programRating";
import { useTheme } from "../../../context/ThemeContext";
import RoundedCheckIcon from "../../../images/icons/rounded-check.inline.svg";
import { dateFormat } from "../../../utils/dateFormat";
import { formatCurrencyAmount, shortcodePreProcessor } from "../../../utils/formatContent";
import { processForm } from "../../../utils/processForm";
import { allowedCalendlyCountries } from "../../../utils/sharedDatasets";
import { FormInputSelect } from "../../shared/form/FormInputSelect";
import { StatusComp } from "../../shared/statusComp";

import "react-datepicker/dist/react-datepicker.css";

const defaultValues = {
  applicant_age: "",
  departure_date: "",
  duration: "",
  booking_time: "",
  best_time_to_call: "",
  grant_value: "",
  grant_funding_fees: [],
  travel_duration: "",
  travel_period: "",
  already_assigned_enrolment_manager: "",
  already_booked_calendly: ""
};

const timeframeOptions = [
  {
    value: "Within 2 weeks",
    label: "Within 2 weeks"
  },
  {
    value: "Within 4 weeks",
    label: "Within 4 weeks"
  },
  {
    value: "Within 3 months",
    label: "Within 3 months"
  },
  {
    value: "Within 6 months",
    label: "Within 6 months"
  },
  {
    value: "6 + months",
    label: "6 + months"
  }
];

const timeOptions = [
  {
    value: "Morning",
    label: "Morning"
  },
  {
    value: "Afternoon",
    label: "Afternoon"
  },
  {
    value: "Evening",
    label: "Evening"
  }
];

const grantOptions = [
  {
    value: "I can cover the full program fees",
    label: "I can cover the full program fees"
  },
  {
    value: "20%",
    label: "20%"
  },
  {
    value: "40%",
    label: "40%"
  },
  {
    value: "60%",
    label: "60%"
  },
  {
    value: "80%",
    label: "80%"
  },
  {
    value: "More than 80%",
    label: "More than 80%"
  }
];

const travelDurationOptions = [
  {
    value: "2 weeks",
    label: "2 weeks"
  },
  {
    value: "4 weeks",
    label: "4 weeks"
  },
  {
    value: "6 weeks",
    label: "6 weeks"
  },
  {
    value: "8 weeks",
    label: "8 weeks"
  },
  {
    value: "12 weeks",
    label: "12 weeks"
  },
  {
    value: "More than 12 weeks",
    label: "More than 12 weeks"
  }
];

const travelPeriodOptions = [
  {
    value: "Within a month",
    label: "Within a month"
  },
  {
    value: "2-3 months from now",
    label: "2-3 months from now"
  },
  {
    value: "4-6 months from now",
    label: "4-6 months from now"
  },
  {
    value: "6-12 months from now",
    label: "6-12 months from now"
  },
  {
    value: "Next year",
    label: "Next year"
  },
  {
    value: "In more than 2 years",
    label: "In more than 2 years"
  }
];

const Step2 = ({ programs, extraDetails = {} }) => {
  const {
    currentSiteMeta,
    peopleDomain,
    planetDomain,
    localeLanguage,
    currentBrand
  } = useTheme();

  const [includeDates, setIncludedDates] = useState([]);
  const [formStatus, setFormStatus] = useState("pending");
  const [durationView, setDurationView] = useState("");
  const [totalCost, setTotalCost] = useState(0);
  const [durationOptions, setDurationOptions] = useState([]);
  const [groupedDates, setGroupedDates] = useState([]);
  const [submitType, setSubmitType] = useState("app_form_submit");
  const [isVirtual, setIsVirtual] = useState(false);
  const [showCalendlyExtraFields, setShowCalendlyExtraFields] = useState("");

  const [programData, setProgramData] = useState({});
  const [programTitle, setProgramTitle] = useState("");

  const [changedDate, setChangedDate] = useState(false);
  const [allowedCalendlyCountry, setAllowedCalendlyCountry] = useState(false);

  const showPricingExtra = false;

  const { genericDetails } = useStaticQuery(graphql`
    query {
      genericDetails: wp {
        globalProgramConfig: gviConfigSettings {
          acfGenericGlobalConfig {
            ...ProgramFeesFragment
          }
        }
      }
    }
  `);

  const programFees =
    genericDetails?.globalProgramConfig?.acfGenericGlobalConfig ?? [];

  const programMinimumFeeDesc =
    programFees?.programMinimumFeeDescription ??
    `GVI programs start at <span class="font-bold">just SHORTCODE_ProgramMinimumFee</span>. Chat with us to find<br/>your perfect fit-we're here to help.`;

  const programMinimumFeeDescription = shortcodePreProcessor({
    programFees,
    currentSiteMeta,
    description: programMinimumFeeDesc
  });

  const programMinimumFeeTitle =
    programFees?.programMinimumFeeTitle ?? "Prefer a cheaper program?";

  const formOptions = {
    defaultValues
    //resolver: yupResolver(validationSchema)
  };
  const form = useForm(formOptions);

  const {
    register,
    getValues,
    handleSubmit,
    reset,
    formState: { errors },
    setValue
  } = form;

  const customStyles = {
    option: (provided, state) => ({
      ...provided,
      color: "white",
      background: "#A9A9A9",
      zIndex: 20
    }),
    control: (provided, state) => ({
      ...provided,
      background: "transparent",
      padding: "4px"
    }),
    singleValue: (provided, state) => {
      const opacity = state.isDisabled ? 0.5 : 1;
      const transition = "opacity 300ms";
      const color = "white";
      const fontSize = "16px";
      const fontWeight = "bold";

      return { ...provided, opacity, transition, color, fontSize, fontWeight };
    },
    placeholder: (defaultStyles) => {
      return {
        ...defaultStyles,
        color: "#ffffff"
      };
    }
  };

  const handleDate = async (programCode = "") => {
    setDurationView("");
    setTotalCost(0);
    setDurationOptions([]);
    setValue("duration", "");

    if (!programCode) {
      setChangedDate(true);
    }

    const dateStr = getValues("departure_date");
    const currency =
      currentSiteMeta.currency ?? process.env.GATSBY_PROJECT_CURRENCY ?? "gbp";

    const dateSelectedDurations = Object.keys(groupedDates)
      .filter((dt) => {
        if (!dateStr) return true;
        return (
          dateFormat({}, new Date(dt)) === dateFormat({}, new Date(dateStr))
        );
      })
      .shift();

    const listOfDurations =
      groupedDates[dateSelectedDurations]
        ?.map((dur) => {
          return {
            value: parseInt(dur.DurationDesc),
            label: dur.DurationDesc,
            price:
              dur.ProductPrices.filter(
                (unitP) =>
                  unitP.CurrencyIso.toLowerCase() === currency.toLowerCase()
              ).shift()?.UnitPrice ?? ""
          };
        })
        .filter((e) => e.value)
        .sort((a, b) => a.value - b.value) ?? [];

    setDurationOptions(listOfDurations);
  };

  const handleDuration = useCallback(() => {
    let selectedDuration = getValues("duration");
    const getPrice = durationOptions
      .filter(
        (pDuration) => parseInt(pDuration.value) === parseInt(selectedDuration)
      )
      .shift();

    setDurationView(
      [selectedDuration, selectedDuration > 1 ? "weeks" : "week"].join(" ")
    );
    setTotalCost(getPrice.price ?? 0);
  }, [durationOptions]);

  const intlNumber = currentSiteMeta?.contact_details?.international ?? "";

  useEffect(() => {
    // eslint-disable-next-line no-restricted-globals
    const currentUrl = new URL(location);
    const programCode =
      extraDetails?.program_id ?? currentUrl?.searchParams.get("program_id");
    const departureDate =
      extraDetails?.departure_date ??
      currentUrl?.searchParams.get("departure_date");
    const duration =
      extraDetails?.duration ?? currentUrl?.searchParams.get("duration");
    const isIngoProgram =
      extraDetails?.flex_program === "true" ||
      currentUrl?.searchParams.get("flex_program") === "true";
    const ingoProviderName =
      extraDetails?.flex_name ??
      currentUrl?.searchParams.get("flex_name") ??
      "";

    if (
      /virtual_program|online_program|virtual_career|virtual_uncc|online_program/.test(
        currentUrl?.search
      )
    ) {
      setIsVirtual(true);
    }

    setProgramData({
      ...programData,
      ...extraDetails,
      lead_id: extraDetails?.lead_id ?? currentUrl?.searchParams.get("lead_id"),
      duration: duration,
      departure_date: departureDate,
      program_code: programCode,
      fundraising:
        extraDetails?.fundraising ??
        currentUrl?.searchParams.get("fundraising"),
      referring_form_type:
        extraDetails?.referring_form_type ??
        currentUrl?.searchParams.get("referring_form_type"),
      flex_program: isIngoProgram,
      flex_name: ingoProviderName
    });

    const controller = new AbortController();
    const signal = controller.signal;

    const useProgramCode = programCode ?? "";
    const currency =
      currentSiteMeta.currency ?? process.env.GATSBY_PROJECT_CURRENCY ?? "gbp";

    if (useProgramCode) {
      const getdates = async () => {
        const res = await fetch(
          `/api/salesforce/programdates?program_code=${useProgramCode}`,
          { signal: signal }
        );

        const durations = await res.json();

        const dateGroup = durations.reduce(
          (
            prevVal,
            currentVal,
            currentIndex,
            theArray,
            keyVal = currentVal.InstanceDate
          ) => (
            (prevVal[keyVal] || (prevVal[keyVal] = [])).push(currentVal),
            prevVal
          ),
          {}
        );

        const reformatDates = Object.keys(dateGroup)
          .map((fDate) => {
            return {
              value: fDate,
              label: fDate
            };
          })
          .filter((date) => {
            const today = new Date();
            let theDate = new Date(date.value);
            return today.getTime() <= theDate.getTime();
          })
          .sort((date1, date2) => {
            return (
              new Date(date1.value).getTime() - new Date(date2.value).getTime()
            );
          });

        const dateDurations = Object.keys(dateGroup)
          .filter((dt) => {
            if (!departureDate) return true;
            return (
              dateFormat({}, new Date(dt)) ===
              dateFormat({}, new Date(departureDate))
            );
          })
          .shift();

        // console.debug("reformatDates", reformatDates);
        // console.debug("dateDurations", dateDurations);

        const listOfDurations =
          dateGroup[dateDurations]
            ?.map((dur) => {
              return {
                value: parseInt(dur.DurationDesc),
                label: dur.DurationDesc,
                price:
                  dur.ProductPrices.filter(
                    (unitP) =>
                      unitP.CurrencyIso.toLowerCase() === currency.toLowerCase()
                  ).shift()?.UnitPrice ?? ""
              };
            })
            .filter((e) => e.value)
            .sort((a, b) => a.value - b.value) ?? [];

        setIncludedDates(reformatDates);
        setGroupedDates(dateGroup);
        setDurationOptions(listOfDurations);
      };

      getdates()
        .then((f) => {
          const departure_date = departureDate;
          if (departure_date) {
            setValue("departure_date", departure_date);
          } else handleDate();
        })
        .catch(console.error);

      return () => {
        controller.abort();
      };
    }
  }, []);

  useEffect(() => {
    // eslint-disable-next-line no-restricted-globals
    const currentUrl = new URL(location);
    const duration =
      extraDetails?.duration ?? currentUrl?.searchParams.get("duration");

    if (durationOptions.length > 0 && duration && !changedDate) {
      const selectedDuration = durationOptions
        .filter((option) => parseInt(option.value) === parseInt(duration))
        .shift();

      setValue("duration", selectedDuration?.value);
      setDurationView(
        [
          selectedDuration?.value,
          selectedDuration?.value > 1 ? "weeks" : "week"
        ].join(" ")
      );
      setTotalCost(selectedDuration?.price ?? 0);
    }
  }, [durationOptions]);

  useEffect(() => {
    if (programData?.program_code) {
      //get the program title & program code and set them
      const theProgram = programs
        .filter(
          (program) =>
            programData.program_code === program?.acfProgramsMeta?.programCode
        )
        .shift();

      setProgramData({
        ...programData,
        program_id: theProgram?.databaseId,
        program_title: theProgram?.title ?? programData?.program_title
      });

      setProgramTitle(theProgram?.title ?? programData?.program_title ?? "");
    }
  }, [programs, programData?.program_code]);

  const onSubmit = useCallback(
    async (data) => {
      try {
        window.scrollTo(0, 0);

        const formFields = {
          parental_consent: "parental_consent",
          privacy_policy: "privacy_policy",
          submit_type: submitType,
          ...programData,
          ...data,
          program_id: programData.program_code,
          form_type: "complete_application"
        };

        // console.debug("Step2  onSubmit >> formFields", formFields, programData);
        // return;
        setFormStatus("processing");
        await processForm({
          formdata: formFields,
          peopleDomain,
          planetDomain,
          currentBrand,
          localeLanguage
        });

        reset();

        //check state data
        let forwardingStateData = {
          ...formFields
        };

        if (typeof window !== "undefined") {
          const collectHistoryData = window?.history?.state ?? {};
          forwardingStateData = {
            ...collectHistoryData,
            hide_calendly: showPricingExtra
          };

          // console.debug("useEffect, step2", forwardingStateData);
        }

        const redirectUrl = [
          "/thank-you-for-your-complete-application/?",
          new Date()?.getTime()
        ].join("");

        const calendlyRedirectUrl = [
          "/thank-you-for-your-application/enrol/?_",
          new Date()?.getTime()
        ]?.join("");

        //step 2 app, adds 2 to lead_score
        const lead_score = parseInt(formFields?.lead_score || 0) + 2;

        const {
          already_assigned_enrolment_manager,
          already_booked_calendly,
          grant_value
        } = formFields ?? {};

        const alreadyEnrolled =
          already_assigned_enrolment_manager === "yes" ||
          already_booked_calendly === "yes";

        const grantValueEqualOrAbove80 = !/80/.test(grant_value) ?? false;

        if (
          !alreadyEnrolled &&
          grantValueEqualOrAbove80 &&
          allowedCalendlyCountry &&
          lead_score >= 10 &&
          (isEnquiry || isApplication) &&
          grantsLayout
        ) {
          await navigate(calendlyRedirectUrl, {
            state: { ...forwardingStateData, redirectUrl }
          });
        } else {
          await navigate(redirectUrl, { state: forwardingStateData });
        }

        // await navigate(redirectUrl, { state: forwardingStateData });
      } catch (err) {
        console.error("Error requestForm :: ", err);
      }
    },
    [
      peopleDomain,
      planetDomain,
      currentBrand,
      localeLanguage,
      programData,
      submitType
    ]
  );

  useEffect(() => {
    const analytics = JSON.parse(
      window?.localStorage.getItem("gvx-analytics") || "{}"
    );
    const country = analytics?.country.toLowerCase();
    const isAllowedCountry = allowedCalendlyCountries.includes(country);
    setAllowedCalendlyCountry(isAllowedCountry);

    console.log("Step 2, country", { country, isAllowedCountry });
  }, []);

  const grantsLayout = !/gvivirtual|gvionline/.test(
    extraDetails?.referring_form_type
  );

  const isEnquiry = /enquiry/.test(extraDetails?.form_type);

  const isApplication = /application/.test(extraDetails?.form_type);

  const leadScore = parseInt(extraDetails?.lead_score || 0) + 2 ?? 0;

  return (
    <div className="px-5 py-6 step-wrapper md:px-16">
      {formStatus === "pending" && (
        <FormProvider {...form}>
          <form
            id={"application-step-2"}
            className="w-full text-center md:text-left md:pb-8"
            onSubmit={handleSubmit(onSubmit)}
          >
            {showPricingExtra ? (
              <>
                <div className="flex flex-col sm:flex-row items-center w-full pt-6 pb-16 sm:pb-6">
                  <div className="flex flex-col w-full">
                    <h2 className={`font-semibold text-white w-full`}>
                      Complete your application
                    </h2>

                    <div className="my-3 text-center sm:text-left text-white w-full">
                      Lastly, please provide us with some additional optional
                      information.
                    </div>
                  </div>

                  <ProgramRating
                    outerContainerClass={"items-center pt-1 pb-4 flex"}
                    ratingIconClass={"fill-white h-5"}
                    ratingLogoClass={"fill-white h-5"}
                  />
                </div>
              </>
            ) : (
              <>
                <h2
                  className={`font-semibold text-white`}
                  dangerouslySetInnerHTML={{
                    __html:
                      isEnquiry && !programTitle
                        ? `Enquiry form`
                        : `Complete your ${isEnquiry ? "enquiry" : "application"}`
                  }}
                />

                <div className="items-center md:items-start block gap-3 pt-6 md:pt-8 pb-3 md:pb-1 text-center md:flex md:text-left">
                  <RoundedCheckIcon className="w-[45px] h-[45px] mt-1 mx-auto mb-2 md:pb-0" />
                  <p className="flex-1 text-white text-mob md:text-base ">
                    {isEnquiry && !programTitle
                      ? "Thank you for your enquiry"
                      : `Thank you for ${isEnquiry ? "enquiring " : "applying "}
                    ${
                      programTitle
                        ? isEnquiry
                          ? `about the ${programTitle}`
                          : `for the ${programTitle}`
                        : ""
                    }`}
                    . One of our Enrollment Managers will contact you within 2
                    hours during our{" "}
                    <span className="font-bold underline">
                      <a
                        href={"/contact-us"}
                        rel={"nofollow"}
                        target={"_gviblank"}
                      >
                        opening hours
                      </a>
                    </span>
                    . If you can&apos;t wait to speak to us, call us on{" "}
                    <span className="font-bold">
                      <a
                        href={`tel:${intlNumber}`}
                        title={`${intlNumber}`}
                        rel="nofollow"
                      >
                        {intlNumber}.{" "}
                      </a>
                    </span>
                  </p>
                </div>

                <p
                  className="py-2 text-white"
                  dangerouslySetInnerHTML={{
                    __html: `Provide additional information below so we can respond more
                  effectively to your ${isEnquiry ? "enquiry" : "application"}.`
                  }}
                />
              </>
            )}

            <div className="gap-3 py-2 md:flex flex-col">
              <div
                className={`${
                  showPricingExtra
                    ? "md:grid-cols-6"
                    : isEnquiry && !programTitle
                      ? " md:grid-rows-2 md:grid-cols-2 py-5"
                      : grantsLayout
                        ? "md:w-full sm:grid-cols-4 md:grid-cols-5 xl:grid-flow-col"
                        : "md:w-full md:grid-cols-6"
                } gap-3 w-full grid-cols-1 md:grid  select-holder`}
              >
                <input
                  type="number"
                  {...register("applicant_age")}
                  placeholder="Your age"
                  min="1"
                  className="col-span-1 h-12 w-full md:px-2 py-4 px-5 mb-3 md:mb-0 text-base rounded-xxs text-white focus:outline-0 bg-transparent border placeholder:text-base placeholder:font-bold placeholder:text-white border-gray-testimonial"
                />

                {isEnquiry && !programTitle ? (
                  <>
                    <div className="w-full col-span-2 flex flex-col sm:flex-row sm:gap-3">
                      <FormInputSelect
                        name="travel_duration"
                        options={travelDurationOptions}
                        styles={customStyles}
                        setValue={setValue}
                        placeholder="How long do you want to travel for?"
                        className={`w-full sm:w-1/2 h-12 mb-3 bg-transparent select-in-step md:mb-0 text-left `}
                        components={{
                          IndicatorSeparator: () => null
                        }}
                      />

                      <FormInputSelect
                        name="travel_period"
                        options={travelPeriodOptions}
                        styles={customStyles}
                        setValue={setValue}
                        placeholder="When do you plan on travelling?"
                        className={`w-full sm:w-1/2 h-12 mb-3 select-in-step md:mb-0 text-left `}
                        components={{
                          IndicatorSeparator: () => null
                        }}
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <FormInputSelect
                      name="departure_date"
                      options={includeDates}
                      handleChange={handleDate}
                      styles={customStyles}
                      setValue={setValue}
                      placeholder="Select a start date"
                      className={`w-full h-12 mb-3 bg-transparent select-in-step md:mb-0 text-left ${
                        showPricingExtra ? "row-start-2 col-span-3" : ""
                      } ${grantsLayout ? "col-span-2 bg-red-500" : "col-span-2"}`}
                      components={{
                        IndicatorSeparator: () => null
                      }}
                    />

                    <FormInputSelect
                      name="duration"
                      options={durationOptions}
                      handleChange={handleDuration}
                      styles={customStyles}
                      setValue={setValue}
                      placeholder="Choose your duration"
                      className={`w-full h-12 mb-3 bg-transparent select-in-step md:mb-0 text-left
                              ${showPricingExtra ? "row-start-2 col-span-3" : ""}
                              ${grantsLayout ? "col-span-2 " : "col-span-3"}`}
                      components={{
                        IndicatorSeparator: () => null
                      }}
                    />

                    {showPricingExtra ? (
                      <div
                        className={`dark:block w-full ${
                          showPricingExtra ? "col-span-full mt-1" : ""
                        }`}
                      >
                        <label
                          htmlFor="motiv"
                          className="pb-2 block text-white"
                        >
                          (Optional) Why do you want to apply for this program?
                        </label>
                        <textarea
                          {...register("why_want_to_apply")}
                          id="motiv"
                          cols="30"
                          rows="5"
                          className="w-full h-16 py-2 pl-[17.5px] mb-3 text-base text-white bg-transparent border focus:outline-0 placeholder:text-white border-gray-testimonial"
                        />
                      </div>
                    ) : (
                      <>
                        {!grantsLayout && (
                          <FormInputSelect
                            name="booking_time"
                            options={timeframeOptions}
                            styles={customStyles}
                            setValue={setValue}
                            placeholder="When do you want to confirm?"
                            className={`w-full h-12 mb-3 bg-transparent select-in-step md:mb-0 text-left ${grantsLayout ? "col-span-2 " : "col-span-3"}`}
                            components={{
                              IndicatorSeparator: () => null
                            }}
                          />
                        )}

                        <FormInputSelect
                          name="best_time_to_call"
                          options={timeOptions}
                          styles={customStyles}
                          setValue={setValue}
                          placeholder="Best time to call you"
                          className={`w-full h-12 mb-3 select-in-step md:mb-0 text-left ${grantsLayout ? "col-span-2" : "col-span-3"}`}
                          components={{
                            IndicatorSeparator: () => null
                          }}
                        />
                      </>
                    )}
                  </>
                )}
              </div>

              {grantsLayout &&
                (isEnquiry && !programTitle ? (
                  <></>
                ) : (
                  <>
                    <div
                      className={`${
                        showPricingExtra
                          ? "md:grid-cols-6"
                          : "md:w-full md:grid-cols-6"
                      } gap-3 w-full grid-cols-1 md:grid  select-holder`}
                    >
                      <div className="w-full col-span-6 mb-3 py-2 text-white text-left">
                        <div className="">
                          What proportion of the program fees would you need to
                          be covered by a grant?
                        </div>
                        <div className="text-sm">
                          GVI Foundation volunteering grants are open to all and
                          awarded based on motivation and circumstance.
                        </div>
                      </div>
                      <FormInputSelect
                        name="grant_value"
                        options={grantOptions}
                        handleChange={(opt) =>
                          setShowCalendlyExtraFields(opt?.value)
                        }
                        styles={customStyles}
                        setValue={setValue}
                        placeholder="Select a grant value"
                        className="w-full h-12 col-span-3 mb-3 select-in-step md:mb-0 text-left"
                        components={{
                          IndicatorSeparator: () => null
                        }}
                      />
                    </div>

                    <div
                      className={`${
                        showPricingExtra
                          ? "md:grid-cols-6"
                          : "md:w-full md:grid-cols-6"
                      } gap-3 w-full grid-cols-1 md:grid  select-holder`}
                    >
                      <div className="w-full col-span-full mb-3 py-2 text-white text-left">
                        If you are intending to apply for a grant, how would you
                        pay for the remaining program fees not covered by the
                        grant?
                        <br /> Select all that apply.
                      </div>
                      <div
                        className={
                          "w-full col-span-full pb-6 pt-2 gap-5 grid grid-cols-1 md:grid-cols-3 text-sm"
                        }
                      >
                        <label className="!flex flex-row w-full select-in-step text-white h-7.5 sm:h-4.5 custom-checkbox items-center gap-3">
                          <input
                            type="checkbox"
                            value="Personal savings"
                            className={`opacity-0 absolute `}
                            {...register("grant_funding_fees", {
                              required: false
                            })}
                          />
                          <span className="checkmark w-7.5 sm:w-4.5 h-7.5 sm:h-4.5 bg-transparent border border-gray-testimonial rounded-xxs flex shrink-0 justify-center items-center" />
                          Personal savings
                        </label>
                        <label className="!flex flex-row w-full select-in-step text-white h-7.5 sm:h-4.5 custom-checkbox items-center gap-3">
                          <input
                            type="checkbox"
                            value="Parents or caregivers"
                            className={`opacity-0 absolute`}
                            {...register("grant_funding_fees", {
                              required: false
                            })}
                          />
                          <span className="checkmark w-7.5 sm:w-4.5 h-7.5 sm:h-4.5 bg-transparent border border-gray-testimonial rounded-xxs flex shrink-0 justify-center items-center" />
                          Parents or caregivers
                        </label>
                        <label className="!flex flex-row w-full select-in-step text-white h-7.5 sm:h-4.5 custom-checkbox items-center gap-3">
                          <input
                            type="checkbox"
                            value="Personal loans or credit cards"
                            className={`opacity-0 absolute`}
                            {...register("grant_funding_fees", {
                              required: false
                            })}
                          />
                          <span className="checkmark w-7.5 sm:w-4.5 h-7.5 sm:h-4.5 bg-transparent border border-gray-testimonial rounded-xxs flex shrink-0 justify-center items-center" />
                          Personal loans or credit cards
                        </label>
                        <label className="!flex flex-row w-full select-in-step text-white h-7.5 sm:h-4.5 custom-checkbox items-center gap-3">
                          <input
                            type="checkbox"
                            value="Inheritance"
                            className={`opacity-0 absolute`}
                            {...register("grant_funding_fees", {
                              required: false
                            })}
                          />
                          <span className="checkmark w-7.5 sm:w-4.5 h-7.5 sm:h-4.5 bg-transparent border border-gray-testimonial rounded-xxs flex shrink-0 justify-center items-center" />
                          Inheritance
                        </label>
                        <label className="!flex flex-row w-full select-in-step text-white h-7.5 sm:h-4.5 custom-checkbox items-center gap-3">
                          <input
                            type="checkbox"
                            value="Fundraising"
                            className={`opacity-0 absolute`}
                            {...register("grant_funding_fees", {
                              required: false
                            })}
                          />
                          <span className="checkmark w-7.5 sm:w-4.5 h-7.5 sm:h-4.5 bg-transparent border border-gray-testimonial rounded-xxs flex shrink-0 justify-center items-center" />
                          Fundraising
                        </label>
                      </div>
                    </div>
                  </>
                ))}

              {leadScore >= 10 && !/80%/gi.test(showCalendlyExtraFields) && (
                <div className="w-full flex flex-col gap-y-2 sm:gap-y-4">
                  <>
                    <div className="w-full col-span-full text-white text-left">
                      Do you already have an assigned Enrolment Manager?*
                    </div>
                    <div
                      className={
                        "w-full col-span-full pb-6 pt-2 gap-5 grid grid-cols-1 md:grid-cols-3 text-sm"
                      }
                    >
                      <label className="!flex flex-row w-full select-in-step text-white h-7.5 sm:h-4.5 custom-checkbox items-center gap-3">
                        <input
                          type="radio"
                          value="yes"
                          className={`opacity-0 absolute ${errors.already_assigned_enrolment_manager ? " !border-red-600 " : ""} `}
                          {...register("already_assigned_enrolment_manager", {
                            required: true
                          })}
                        />
                        <span
                          className={`checkmark w-7.5 sm:w-4.5 h-7.5 sm:h-4.5 bg-transparent border border-gray-testimonial rounded-xxs flex shrink-0 justify-center items-center ${errors.already_assigned_enrolment_manager ? " !border-red-600 " : ""}`}
                        />
                        Yes
                      </label>
                      <label className="!flex flex-row w-full select-in-step text-white h-7.5 sm:h-4.5 custom-checkbox items-center gap-3">
                        <input
                          type="radio"
                          value="no"
                          className={`opacity-0 absolute ${errors.already_assigned_enrolment_manager ? " !border-red-600 " : ""}`}
                          {...register("already_assigned_enrolment_manager", {
                            required: true
                          })}
                        />
                        <span
                          className={`checkmark w-7.5 sm:w-4.5 h-7.5 sm:h-4.5 bg-transparent border border-gray-testimonial rounded-xxs flex shrink-0 justify-center items-center ${errors.already_assigned_enrolment_manager ? " !border-red-600 " : ""}`}
                        />
                        No
                      </label>
                    </div>
                  </>
                  <>
                    <div className="w-full col-span-full text-white text-left">
                      Have you already booked a Calendly meeting?*
                    </div>
                    <div
                      className={
                        "w-full col-span-full pb-6 pt-2 gap-5 grid grid-cols-1 md:grid-cols-3 text-sm"
                      }
                    >
                      <label className="!flex flex-row w-full select-in-step text-white h-7.5 sm:h-4.5 custom-checkbox items-center gap-3">
                        <input
                          type="radio"
                          value="yes"
                          className={`opacity-0 absolute ${errors.already_booked_calendly ? " !border-red-600 " : ""}`}
                          {...register("already_booked_calendly", {
                            required: true
                          })}
                        />
                        <span
                          className={`checkmark w-7.5 sm:w-4.5 h-7.5 sm:h-4.5 bg-transparent border border-gray-testimonial rounded-xxs flex shrink-0 justify-center items-center ${errors.already_booked_calendly ? " !border-red-600 " : ""}`}
                        />
                        Yes
                      </label>
                      <label className="!flex flex-row w-full select-in-step text-white h-7.5 sm:h-4.5 custom-checkbox items-center gap-3">
                        <input
                          type="radio"
                          value="no"
                          className={`opacity-0 absolute ${errors.already_booked_calendly ? " !border-red-600 " : ""}`}
                          {...register("already_booked_calendly", {
                            required: true
                          })}
                        />
                        <span
                          className={`checkmark w-7.5 sm:w-4.5 h-7.5 sm:h-4.5 bg-transparent border border-gray-testimonial rounded-xxs flex shrink-0 justify-center items-center ${errors.already_booked_calendly ? " !border-red-600 " : ""}`}
                        />
                        No
                      </label>
                    </div>
                  </>
                </div>
              )}

              {!showPricingExtra && (
                <div
                  className={`md:w-full gap-3 w-full grid-cols-1 md:grid  select-holder ${grantsLayout || !isVirtual ? "md:grid-cols-7" : "md:grid-cols-6"}`}
                >
                  <div
                    className={`app-form-container ${isEnquiry && !programTitle ? "bg-transparent !border-none" : "bg-white dark:bg-gray-dark"} justify-between w-full px-0 sm:px-6 py-4 mb-3 md:mb-0 selected-board ${grantsLayout || !isVirtual ? "col-span-full lg:col-start-3 lg:col-span-3" : "col-span-3"} `}
                  >
                    <div
                      className={`${isEnquiry && !programTitle ? "hidden" : ""} flex justify-center text-center text-black-lighter dark:text-white gap-4 md:gap-6 w-full pt-2 sm:pt-2`}
                    >
                      <div>
                        <p className="text-xs dark:text-[#A8A8A8]">Duration</p>
                        <span className="font-bold">{durationView} </span>
                      </div>
                      <div className="pl-4 md:pl-6 border-l border-gray-darken">
                        <p className="text-xs dark:text-[#A8A8A8]">
                          Total cost {grantsLayout ? "before grant" : ""}
                        </p>
                        <span
                          className="font-bold"
                          dangerouslySetInnerHTML={{
                            __html: formatCurrencyAmount({
                              currentSiteMeta,
                              amount: totalCost
                            })
                          }}
                        />
                      </div>
                    </div>
                    <div className="flex justify-center w-full pt-2 sm:pt-6">
                      <CtaButton
                        ctaLabel={`${
                          isEnquiry && !programTitle
                            ? "Continue"
                            : showPricingExtra
                              ? "Complete application"
                              : "Submit"
                        }`}
                        preset={"primary"}
                        labelClass="text-base leading-genericCta pr-2 font-bold"
                        extraButtonStyle={`${isEnquiry && !programTitle ? "w-full" : ""} min-w-56 px-8`}
                        arrowClass={
                          "stroke-white group-hover:stroke-black-lighter dark:group-hover:stroke-green-standard dark:hover:stroke-green-standard dark:stroke-gray-darker stroke-white hover:stroke-black-lighter group-hover:stroke-black-lighter"
                        }
                        showAsButton={true}
                      />
                    </div>
                  </div>

                  {!grantsLayout && isVirtual && (
                    <div
                      className={`app-form-container col-span-3 justify-between w-full px-6 py-4 mb-3 md:mb-0 selected-board `}
                    >
                      <div className="flex justify-center">
                        <span
                          className="font-bold text-white"
                          dangerouslySetInnerHTML={{
                            __html: programMinimumFeeTitle
                          }}
                        />
                      </div>
                      <div className="flex flex-col justify-center text-center text-white text-xs">
                        <span
                          className=""
                          dangerouslySetInnerHTML={{
                            __html: programMinimumFeeDescription
                          }}
                        />
                      </div>
                      <div className="flex justify-center w-full pt-2 sm:pt-4">
                        <CtaButton
                          ctaLabel={"Speak to us"}
                          preset={"secondary"}
                          extraButtonStyle={
                            "min-w-56 px-8 border-white text-white dark:bg-transparent dark:hover:bg-white"
                          }
                          arrowClass={
                            "stroke-black-lighter hover:stroke-white group-hover:stroke-white dark:stroke-white dark:hover:stroke-gray-darker dark:group-hover:stroke-gray-darker stroke-white group-hover:stroke-black-light "
                          }
                          showAsButton={true}
                          onClick={() => {
                            setSubmitType("speak_to_us");
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </form>
        </FormProvider>
      )}

      {formStatus === "processing" && (
        <>
          <div className="m-auto py-12 w-3/4 ">
            <StatusComp
              expText={"Just a moment..."}
              statusText={`We’re making sure we have everything we need before connecting you with one of our team members!`}
              status={formStatus}
            />
          </div>
        </>
      )}
    </div>
  );
};
export default Step2;
